import EXIF from 'exif-js';
import promisify from 'util-promisify';

type ImageInfo = {
	image: string;
	sizeInfo: {
		width: number;
		height: number;
		ratio: number;
	};
};

const thumbnailFromUrl = (
	imageURL: string,
	width: number,
	height: number | null = null,
): Promise<ImageInfo> => {
	return new Promise((resolve, reject) => {
		const sizeInfo = {
			width: 0,
			height: 0,
			ratio: 1,
		};

		const img = document.createElement('img');

		img.onload = async function () {
			const exifData = await Promise.race([
				new Promise((resolve) => setTimeout(resolve, 1000)),
				promisify(EXIF.getData.bind(EXIF))(img),
			]);

			const orientation = exifData ? EXIF.getTag(exifData, 'Orientation') : null;

			sizeInfo.width = img.width;
			sizeInfo.height = img.height;
			sizeInfo.ratio =
				sizeInfo.width > 0 && sizeInfo.height > 0 ? sizeInfo.height / sizeInfo.width : 1;

			const frame = {
				x: 0,
				y: 0,
				width,
				height: Math.round(width * sizeInfo.ratio),
			};

			if (height) {
				const ratio = sizeInfo.ratio;
				const targetRatio = height / width;

				if (ratio >= targetRatio) {
					frame.height = height;
					frame.width = Math.round(frame.height / ratio);
					frame.x = Math.round((width - frame.width) / 2);
				} else {
					frame.width = width;
					frame.height = Math.round(frame.width * ratio);
					frame.y = Math.round((height - frame.height) / 2);
				}
			}

			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');

			if (!ctx) {
				return reject(new Error('Image load failed'));
			}

			canvas.width = width;
			canvas.height = height || frame.height;

			if (height) {
				ctx.fillStyle = '#FFFFFF';
				ctx.rect(0, 0, width, height || frame.height);
				ctx.fill();
			}

			ctx.save();

			if (orientation) {
				width = canvas.width;
				height = canvas.height;

				const styleWidth = canvas.style.width;
				const styleHeight = canvas.style.height;

				if (orientation > 4) {
					canvas.width = height;
					canvas.style.width = styleHeight;
					canvas.height = width;
					canvas.style.height = styleWidth;
				}

				switch (orientation) {
					case 2:
						ctx.translate(width, 0);
						ctx.scale(-1, 1);
						break;
					case 3:
						ctx.translate(width, height);
						ctx.rotate(Math.PI);
						break;
					case 4:
						ctx.translate(0, height);
						ctx.scale(1, -1);
						break;
					case 5:
						ctx.rotate(0.5 * Math.PI);
						ctx.scale(1, -1);
						break;
					case 6:
						ctx.rotate(0.5 * Math.PI);
						ctx.translate(0, -height);
						break;
					case 7:
						ctx.rotate(0.5 * Math.PI);
						ctx.translate(width, -height);
						ctx.scale(-1, 1);
						break;
					case 8:
						ctx.rotate(-0.5 * Math.PI);
						ctx.translate(-width, 0);
						break;
					default:
						break;
				}
			}

			ctx.drawImage(
				img,
				0,
				0,
				sizeInfo.width,
				sizeInfo.height,
				frame.x,
				frame.y,
				frame.width,
				frame.height,
			);

			ctx.restore();

			resolve({ sizeInfo, image: canvas.toDataURL() });
		};

		img.onerror = function () {
			reject(new Error('Image load failed'));
		};

		img.src = imageURL;
	});
};

export default function thumbnail(
	file: File,
	width: number,
	height: number | null = null,
): Promise<ImageInfo> {
	return new Promise((resolve, reject) => {
		const fileReader = new FileReader();

		fileReader.onload = async function () {
			if (file.type === 'image/svg+xml') {
				return resolve({
					sizeInfo: {
						width: 0,
						height: 0,
						ratio: 1,
					},
					image: String(fileReader.result),
				});
			}

			try {
				const info = await thumbnailFromUrl(String(fileReader.result), width, height);
				resolve(info);
			} catch (e) {
				reject(e);
			}
		};

		fileReader.onerror = function () {
			reject(new Error('Image load failed'));
		};

		fileReader.readAsDataURL(file);
	});
}
